// src/pages/Marketplace/markets/MarketsPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Link } from 'react-router-dom';
import { acquireApiToken } from '@/auth/acquireApiToken';
import { Env, ENV_COLORS, ErrorBanner, Spinner, getAdminApiUrl } from '@/pages/Admin/shared';
import { useMarkets } from '@/pages/Marketplace/shared';
import MarketDetailPanel from './MarketDetailPanel';
import CreateMarketModal from './CreateMarketModal';

const LS_KEY_ENV = 'marketplace.env';
const LS_KEY_SELECTED = 'marketplace.markets.selectedId';

export default function MarketsPage(): React.ReactElement {
  const { instance, accounts } = useMsal();
  const [env, setEnv] = useState<Env>(() => (localStorage.getItem(LS_KEY_ENV) as Env) || 'dev');
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(
    () => localStorage.getItem(LS_KEY_SELECTED),
  );
  const [creating, setCreating] = useState(false);

  useEffect(() => { localStorage.setItem(LS_KEY_ENV, env); }, [env]);
  useEffect(() => {
    if (selectedId) localStorage.setItem(LS_KEY_SELECTED, selectedId);
  }, [selectedId]);

  useEffect(() => {
    if (accounts.length === 0) return;
    acquireApiToken(instance, accounts[0]).then((accessToken) => ({ accessToken }))
      .then((r) => setAccessToken(r.accessToken))
      .catch(() => setAccessToken(null));
  }, [instance, accounts]);

  const apiUrl = getAdminApiUrl(env);
  const { data: markets, loading, error, refetch } = useMarkets(apiUrl, accessToken);

  const selectedMarket = useMemo(
    () => markets?.find((m) => m.id === selectedId) ?? null,
    [markets, selectedId],
  );

  const envColors = ENV_COLORS[env];

  return (
    <div className="flex min-h-[calc(100vh-72px)] bg-gray-950 text-gray-200">
      {/* Left rail */}
      <aside className="w-[300px] border-r border-gray-800 p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">Markets</h2>
          <select
            value={env}
            onChange={(e) => setEnv(e.target.value as Env)}
            className={`text-xs px-2 py-1 rounded border ${envColors.pill}`}
          >
            <option value="dev">dev</option>
            <option value="staging">staging</option>
            <option value="prod">prod</option>
          </select>
        </div>

        <button
          className="px-3 py-2 text-sm font-medium bg-violet-600 hover:bg-violet-700 rounded-lg"
          onClick={() => setCreating(true)}
        >
          + Create Market
        </button>

        <Link
          to="/markets/unassigned"
          className="text-xs text-amber-300 hover:text-amber-200 px-2 py-1 rounded bg-amber-950/40 border border-amber-800/50"
        >
          View unassigned businesses →
        </Link>

        {loading && <Spinner size="sm" />}
        {error && <ErrorBanner message={error} />}

        <ul className="flex flex-col gap-1 overflow-y-auto mt-1">
          {markets?.map((m) => (
            <li key={m.id}>
              <button
                onClick={() => setSelectedId(m.id)}
                className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between ${
                  m.id === selectedId ? 'bg-gray-800 text-violet-300' : 'hover:bg-gray-800/60'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span>{m.kind === 'college' ? '🎓' : '🏙️'}</span>
                  <span className="text-sm">{m.name}</span>
                </span>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full border ${
                    m.is_active
                      ? 'border-emerald-700/50 text-emerald-300 bg-emerald-950/40'
                      : 'border-gray-700 text-gray-400 bg-gray-900'
                  }`}
                >
                  {m.is_active ? 'active' : 'inactive'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* Detail */}
      <main className="flex-1 p-6 overflow-y-auto">
        {selectedMarket ? (
          <MarketDetailPanel
            market={selectedMarket}
            apiUrl={apiUrl}
            env={env}
            accessToken={accessToken}
            onChanged={refetch}
          />
        ) : (
          <div className="text-gray-500 text-sm">Select a market from the left rail.</div>
        )}
      </main>

      {creating && (
        <CreateMarketModal
          apiUrl={apiUrl}
          accessToken={accessToken}
          onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); setSelectedId(id); setTimeout(refetch, 2000); refetch(); }}
        />
      )}
    </div>
  );
}
